import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import './PatientCarePlan.css';
import { MDXEditor, headingsPlugin, listsPlugin, quotePlugin, thematicBreakPlugin, markdownShortcutPlugin, toolbarPlugin, UndoRedo, BoldItalicUnderlineToggles, BlockTypeSelect, CreateLink, InsertTable, ListsToggle, Separator, InsertThematicBreak } from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { generateCarePlanPDF } from './CarePlanPDF';
import ProfilePhotoUpload from './ProfilePhotoUpload';
import { appName, orgName } from '../brand';

const carePlanSections = [
  {
    id: 'one-page-profile',
    title: 'One Page Profile',
    subsections: [
      { 
        id: 'about-myself', 
        title: 'Let Me Tell You About Myself',
        explainerText: 'In this section write a couple of paragraphs about the individual in first person format introducing themselves to the reader. This section should be fun, uplifting and capture the individual\'s personality. We want someone who is reading this support plan to have a good first impression of the individual. Introduce the individual by saying hello to the reader and giving their name and possibly their age. Write about the person\'s hobbies, their interests and their favourite things to do. You can also share things that the individual might be very proud of (i.e. working out, losing weight, making a new friend etc.) or perhaps a recent experience that the individual looks upon fondly and likes to share with others (i.e. a birthday party, trip to West Edmonton Mall, going to summer camp etc.).\n\n**I need to be respected as a valuable person, to maintain my dignity, to be empowered to make choices, to take risks similar to my peers, and to identify my talents. People who know me well have written the rest of this document based on our experiences together. I have participated as much as I could. Its purpose is to help you understand who I am and what I need. Please read it and know that I need you to be committed to excellence, use good judgment, and recognize that I am doing the best I can.**',
        highlightByDefault: true
      }
    ]
  },
  {
    id: 'contact-information',
    title: 'Personal/Medical Contact Information',
    subsections: []
  },
  {
    id: 'personal-biography',
    title: 'Personal Biography',
    subsections: [],
    explainerText: 'In this section we want to capture as much of the individual\'s history as possible. We are trying to give information about the individual\'s journey prior to coming to {{ORG}}, and in some instances (if the person has been with {{ORG}} for several years) how their story has continued to unfold since being part of {{ORG}}. Typical information points this section would include are: when and where the person was born, an overview of who their immediate family consists of, where they went to school, any cultural background that is important to the person, when they first entered care or started receiving formal support, listing the different places they have lived, the circumstances surrounding different moves or living situations (i.e. behaviour struggles, substance misuse etc.), any traumas the person has endured (i.e. death of loved ones, bullying, physical, emotional or sexual abuse etc.), when they arrived at {{ORG}}, what their living situation is within {{ORG}} (i.e. roommates), any significant events experienced while at {{ORG}} for better or worse.\n\nThe main purpose of this section is to give supports perspective around where this individual has come from and the path that they have walked up until this point.'
  },
  {
    id: 'quality-of-life',
    title: 'Quality of Life Domains',
    subsections: [
      { 
        id: 'physical-wellbeing', 
        title: 'Fundamental Values: Physical Wellbeing',
        explainerText: 'How does the individual view their own bodily integrity and health? Do they understand the importance of living a healthy lifestyle? Looking good, feeling good etc.? Are they cognizant of these things and do they do them on their own? Or are they unaware and need the team to help ensure it is happening by using role modelling, reminders, prompts etc.\n\nNote** Don\'t get into specifics at this time around things like the clothes they like to wear, food they like to eat or hygiene routines. These details will be discussed in greater depth in later sections.'
      },
      { 
        id: 'emotional-wellbeing', 
        title: 'Fundamental Values: Emotional Wellbeing',
        explainerText: 'Does the individual believe they are important and worthy? Or do they have a negative sense of self where they may speak negatively about themselves? Does the individual generally seem to feel safe and comfortable around others or are they fearful/hesitant? Is the individual able to relax? In what circumstances are they the most at peace? Is there any trauma in their past or present that they are carrying with them currently? What does the individual need from their support people to increase harmony and decrease the impact of trauma?'
      },
      { 
        id: 'self-determination', 
        title: 'Fundamental Values: Self Determination',
        explainerText: 'Does the individual have the opportunity to make their own choices and decisions? Does the individual have a history or tend to engage in power struggles? How should supports approach decision making with this individual? What support do they need to make positive and healthy decisions? How are we empowering the individual to have autonomy and control over their life?'
      },
      { 
        id: 'personal-development', 
        title: 'Fundamental Values: Personal Development',
        explainerText: 'Does the individual express goals and dreams for their future? Or do they seem to exist more in the present? Do they speak confidently and proudly about their gifts, talents and accomplishments? What skills would the individual like to develop? Does the individual like to learn new things and have new opportunities? What support do they require in order to make learning goals a reality? How are we promoting continuous learning with this individual?'
      },
      { 
        id: 'interpersonal-relations', 
        title: 'Fundamental Values: Interpersonal Relations',
        explainerText: 'What is their relationship with their family? Do they have any friends? If so, who? Where does the person feel the greatest sense of belonging? Is there anyone in this person\'s life outside of paid relationships? What does the individual need from supports to help connect them to a greater sense of belonging? Does the individual have or desire any romantic relationships?'
      },
      { 
        id: 'social-inclusion', 
        title: 'Fundamental Values: Social Inclusion',
        explainerText: 'What does the individual do to be social? Do they belong to any groups? Is there any place in particular they like to go in the community to connect? Are there people that they invite or have over to their home to interact with socially? How does the person like to connect socially – do they like large events and crowds or small more intimate gatherings etc.?'
      },
      { 
        id: 'material-wellbeing', 
        title: 'Fundamental Values: Material Wellbeing',
        explainerText: 'How does the individual feel about their financial and material situation? Do they have access to their money and are they able to spend as they would like? Do they have any possessions that are near and dear to them? Does the individual feel happy with their home and have everything that they need in their home and life to feel content/comfortable? Are there any items that the individual desires that they do not have? How can the support team help the individual achieve these desires?'
      },
      { 
        id: 'rights', 
        title: 'Fundamental Values: Rights',
        explainerText: 'Does the individual understand their rights and how are they made aware of them? How are the individual\'s values and life choices respected? How does the individual served experience equal opportunity? Are there any barriers that need to be overcome in order for them to have opportunities? How is it apparent the individual is being treated with dignity and respect?'
      },
      { 
        id: 'personal-environment', 
        title: 'Personal Environment',
        explainerText: 'What does the individual like in their environment? Think about their home and their bedroom/living space, how do they prefer it to look? Do they have it decorated in any certain way i.e. paint colour, specific themes of décor etc. Do they appreciate a well-organized calm/quiet environment? Do they like a livelier environment i.e. to have music playing regularly? Do they like to spend time privately? Do they have preferred furniture that they like to use or a specific place they like to sit? Do they like an open space for activities such as exercise or dancing? Is it important to them that it is clean/tidy Etc.\n\nNote** Please don\'t include environmental factors in this section such as safety measures i.e. locking up knives or sharps or safety measures in the layout of the home. This will come later in the Crisis Prevention/Intervention environmental section.'
      },
      { 
        id: 'personal-supports', 
        title: 'Personal Supports',
        explainerText: 'Outline who is the ideal support person for this individual? What are the key characteristics that a support should have in order to be successful with this individual? I.e. male/female, tall/short, sense of humour, outgoing/reserved, talkative/quiet, etc. Does the support need to be able to be assertive at times? Creative? Athletic? Artistic? Younger/older? Etc.'
      }
    ]
  },
  {
    id: 'health-wellness',
    title: 'Health & Wellness',
    subsections: [
      { 
        id: 'general-health', 
        title: 'General Health',
        explainerText: 'How is the individual\'s general health? Do they have any frequent or ongoing health concerns? What is "normal" for them? What are indicators that the person does not feel well and what should support people do when they see them?'
      },
      { 
        id: 'surgical-therapeutic', 
        title: 'Surgical/Therapeutic',
        explainerText: 'What is the individual\'s surgical/therapeutic history? Does the person have any ongoing therapeutic needs? (Such as physio, chiropractor, occupational therapy, or other regular involvement with a specialist of any kind) What are the indicators that an appointment needs to be made?'
      },
      { 
        id: 'medical-equipment', 
        title: 'Medical Equipment Requirements',
        explainerText: 'Does the person use any medical equipment such as nebulizers, CPAP machine, blood pressure monitor, blood glucose monitor, oxygen etc.? If yes, please describe.'
      },
      { 
        id: 'medical-appointments', 
        title: 'Medical Appointments',
        explainerText: 'What is the procedure and who is responsible for booking and attending medical appointments? Are there any support tips to increase the likelihood that appointments will go smoothly? Any things to be aware of that may make an appointment not go smoothly? Does anyone need to be contacted regarding medical appointments or their outcome i.e. family, {{ORG}} Health Team, social worker etc.?'
      },
      { 
        id: 'mental-health', 
        title: 'Mental Health',
        explainerText: 'What is the individual\'s mental health diagnosis if they have one? Who do they see as a psychiatrist and often do they see them? When they are struggling with their mental health, how does it generally impact them or manifest itself? Speak to the specific factors that are important to the individual\'s mental health i.e. medication, sleep, internal/external factors, family contact, substance use etc. and how these could impact them in both a positive or negative way. Do they see any other practitioners in support of their mental health i.e. counsellor, elder etc. Does the individual have suicidal ideation or talk about harming themselves when they are struggling?\n\nNote** If the individual does not have a mental health diagnosis and does not see a psychiatrist this section can be deleted or amended to speak to their general mental well-being.'
      },
      { 
        id: 'diet', 
        title: 'Diet',
        explainerText: 'What is the individual\'s diet like? What foods do they like/dislike? Do they eat a regular diet or do they have a special diet (ground food, supplemented by meal replacements, diabetes, high fiber, low salt, etc.)? Does the person have any foods that they cannot eat due to medications or allergies? What does the person need from support people to ensure that they get the nutrition they need from their diet and still enjoy their food?'
      },
      { 
        id: 'mobility', 
        title: 'Mobility',
        explainerText: 'What is individual\'s mobility like? How independent are they in moving around and what do they need from the support person to maintain and/or improve their mobility? Do they use any equipment to aid or enhance their mobility i.e. wheelchair, grab bars, walker, lift equipment, slider sheets etc.'
      },
      { 
        id: 'medication-needs', 
        title: 'Medication Needs',
        explainerText: 'What does the individual needs from support people to ensure that they take their medications properly? Generally in this section you will just talk about having to provide the individual with their medication at the proper times or if there is any particular way medication is given i.e. on a spoon with yogurt etc.'
      },
      { 
        id: 'vision-hearing', 
        title: 'Vision, Hearing',
        explainerText: 'Complete vision, hearing, and seizure assessments with checkboxes and additional comments for each area.'
      }
    ]
  },
  {
    id: 'emotional-development',
    title: 'Personal/Emotional Development',
    subsections: [
      { 
        id: 'profile', 
        title: 'Profile',
        explainerText: 'What is the individual\'s general emotional state? How does the individual communicate their feelings? What do they do or look like when they are feeling sad or upset? What do they do or look like when they are happy? How does the individual develop relationships?'
      },
      { 
        id: 'emotional-needs', 
        title: 'Emotional Needs',
        explainerText: 'What does the individual need from support people to help them regulate their emotions, understand emotions, process things, deal with pain, share pleasure, use their strengths and generally be happier?'
      },
      { 
        id: 'spiritual-needs', 
        title: 'Spiritual Needs or Preferences',
        explainerText: 'Does the individual belong to any particular faith or adhere to any cultural practices? How do they practice it? What do they need from support people to respect their beliefs and facilitate their desired level of participation?'
      },
      { 
        id: 'dating-relationships', 
        title: 'Dating & Personal Relationships',
        explainerText: 'Does the individual have a partner or an interest in dating or having romantic relationships? What support do they need from their team in order for these relationships to be healthy and positive? Are there any elements to the relationship that need to be supervised? Does the individual need any sort of education or guidance around sexuality, relationships, consent, sex or sexual health?\n\nNote ** If the individual has no desire to date or have romantic relationships this section can be left incomplete.'
      },
      { 
        id: 'independence', 
        title: 'Independence',
        explainerText: 'What are the individuals\' supervision needs? How close does the support person need to be to them in different situations? Under what circumstances (if any) can they be by themselves? What factors need to be considered? Under what circumstances should the person be considered missing and activate the Missing Person\'s plan?'
      }
    ]
  },
  {
    id: 'personal-care',
    title: 'Personal Care',
    subsections: [
      { 
        id: 'personal-hygiene', 
        title: 'Personal Hygiene – General Information',
        explainerText: 'In this general information section please share a short synopsis of how the individual views their personal hygiene. Do they seem to care about it and hold it in great importance or are they indifferent? Do they require modelling or prompts to engage in hygiene routines? Is there anything that needs to be included here that is not in the sections below?'
      },
      { 
        id: 'bathing', 
        title: 'Bathing',
        explainerText: 'Is the individual agreeable to bathing or do they resist? How often do they bathe? Do they prefer to bath or shower? Do they require any assistance with bathing? Is there a routine around bathing? Do they require prompts or supervision in order to clean themselves properly? Etc.'
      },
      { 
        id: 'skin-care', 
        title: 'Skin Care',
        explainerText: 'This section should include information around shaving, lotions, any skin conditions, and what assistance or prompts are needed; risks of skin breakdown (pressure sores), or picking, or self-injury, such as biting and what to watch for, as well as what to do if it happens in order to care for the individual\'s skin and help it heal.'
      },
      { 
        id: 'dental', 
        title: 'Dental',
        explainerText: 'Describe the condition of the individual\'s teeth. Does the individual brush and floss regularly? How often do they see the dentist/hygienist? What support does the individual need in order to maintain proper dental hygiene?'
      },
      { 
        id: 'dressing', 
        title: 'Dressing',
        explainerText: 'Does the individual have a favourite type of clothing or certain clothing that they prefer to wear? Are they able to communicate what they want to wear or pick out their own clothes for the day? Do they need prompts or direction around dressing? Without guidance would they wear the same clothes day after day or clothes that are dirty, tattered or too small? Does the individual understand how to dress appropriately for different types of weather?'
      },
      { 
        id: 'toileting', 
        title: 'Toileting',
        explainerText: 'Can the individual use the bathroom independently or do they require full or partial assistance? Do they have any incontinence issues? Wear attends? Do they need to use the bathroom more frequently than the average person? Are there any cleaning needs that should take place after the person has used the bathroom? Do they flush? Do they wash their hands afterward? etc.'
      },
      { 
        id: 'sleeping', 
        title: 'Sleeping',
        explainerText: 'What are the individual\'s normal sleeping patterns? What are their sleep needs? What impact does sleep or lack thereof have on the person\'s day? What are their bedtime routine? Do they have trouble waking up in the morning? Do they like to sleep in? Anything else supports need to be aware of in regard to sleep?'
      },
      { 
        id: 'eating', 
        title: 'Eating',
        explainerText: 'How does the individual eat and do they have any support needs around eating, such as using a special spoon, assistance cutting meat, assistance transferring food from the plate to their mouth, what can they eat independently, what do they need help with, how do they indicate that they don\'t want any, or that they are full, or that they want something else? Etc.'
      },
      { 
        id: 'mobility-care', 
        title: 'Mobility',
        explainerText: 'Does the individual have any mobility needs? Do they use a wheelchair? Are they able to get around on their own? Do they have any issues with balance or need any physical support from either people or mechanical equipment?'
      }
    ]
  },
  {
    id: 'communication',
    title: 'Communication & Learning',
    subsections: [
      { 
        id: 'receptive', 
        title: 'Receptive',
        explainerText: 'What types of communication does the individual understand? How perceptive are they to non-verbal cues? Does the individual cooperate with requests? How complex? What is the individuals\' understanding of emotions in others? Do they take extra time to process before responding? How do they need to be communicated with if they are having trouble understanding something i.e. need a verbal que or an example to be given, writing it down etc.? Are they aware and perceptive of things going on around them in their environment? Is there anything in-particular you need to consider when giving this person input that could make communicating with them easier i.e. make eye contact, sit down together, speak slowly, be in a quiet space etc.'
      },
      { 
        id: 'expressive', 
        title: 'Expressive',
        explainerText: 'Is the individual verbal or nonverbal? How does the individual express their thoughts, feelings, wants and needs? How do they communicate that they don\'t understand something? How does the person say no? What increases the individual\'s desire to communicate with someone? What can decrease it? Are they difficult or easy to understand? Do they understand how to express what they are feeling appropriately? How do they express pain? Will they share if something is bothering them?'
      },
      { 
        id: 'learning-preferences', 
        title: 'Learning Preferences',
        explainerText: 'Detail how the individual learns best, including their preferred learning styles (visual, auditory, hands-on), pace of learning, and what teaching methods are most effective. Include their interests in learning new skills or information.'
      },
      { 
        id: 'assistive-technology', 
        title: 'Assistive Technology',
        explainerText: 'Document any assistive technology or communication devices used, including tablets, communication boards, or specialized software. Include information about training needs, maintenance, and how these tools support their communication and learning.'
      }
    ]
  },
  {
    id: 'community-inclusion',
    title: 'Community Inclusion and Lifestyle Options',
    subsections: [
      { 
        id: 'household-activities', 
        title: 'Household Activities',
        explainerText: 'How does the individual participate in the upkeep of their home? How can support people include the individual in tasks even when they choose not to participate so that support people are not ignoring the individual while spending their time cleaning or doing chores?'
      },
      { 
        id: 'leisure-recreation', 
        title: 'Leisure/Recreation',
        explainerText: 'Interests:\nWhat does the individual like to do? What are they interested in trying? This could be while they are at home or also in the community. Are there any events put on by {{ORG}} that they like to attend or would like to try? Is there anything they like to do outside of {{ORG}} activities?\n\nNeeds:\nWhat does the individual need from supports to engage in leisure activities both at home and in the community? Do they self-generate different activities? Do they make their own plans, and get themselves where they need to go? Do they need constant supervision, interaction or guidance when participating in activities? Do they need the support person to show enthusiasm to get them to engage? Do they like having personal space for certain activities etc.?'
      }
    ]
  },
  {
    id: 'employment-volunteer',
    title: 'Employment/Volunteer/Day Program Information',
    subsections: [
      { 
        id: 'present-employment', 
        title: 'Present Employment/Day Program',
        explainerText: 'Where does the individual currently work or go to a Day Program? How long have they been there? What does their job entail? How many hours per week do they work? What supports are in place to help the person be successful? Do they have a support or job coach that goes with them? Etc.\n\nNote** If the individual is not currently working or going to a day program but perhaps has hopes of working in the future you can put an N/A in this section. If the individual has no desire to have a paid job or the nature of their disability would not allow them to have a job you can delete this section.'
      },
      { 
        id: 'former-employment', 
        title: 'Former Employment',
        explainerText: 'List any former employers/employment or day program opportunities that the individual has had and any pertinent information as to why it ended or perhaps was unsuccessful.'
      },
      { 
        id: 'goals-future-employment', 
        title: 'Goals for Future Employment',
        explainerText: 'Does the individual have a goal or dream job that they are striving toward? What would they like to be doing for work? Is it a possibility? How close could we get them to realizing their dream job? What options are we exploring to move them closer to their preferred employment?'
      },
      { 
        id: 'employment-needs', 
        title: 'Needs',
        explainerText: 'Fill in any needs the individual may have in regard to employment/day program that have not been already stated or listed above. If there is nothing else you can delete this "needs" heading.'
      }
    ]
  },
  {
    id: 'continuing-education',
    title: 'Continuing Education Activities',
    subsections: [
      { 
        id: 'current-education', 
        title: 'Current Education',
        explainerText: 'If the individual is still in school, list all basic school related information here, i.e. What school they go to, who their teacher is, size of class, what a typical day of school looks like, what time they arrive and leave school, how many years of school they have left etc.\n\n**Note: If the individual is no longer in school this section can be left blank'
      },
      { 
        id: 'educational-courses', 
        title: 'Educational Courses Participated In',
        explainerText: 'Are there any educational courses outside of school that the individual has participated in? If so, list each course in a separate heading and give a brief description of what the course entailed.\n\nExample: Tell It Like It Is: "Tell it Like it Is" is a 10 week course that covers topics such as proper personal hygiene, emotional identification, healthy relationships, and sexual topics such as masturbation, safe sex, consent, pregnancy and sexually transmitted infections amongst others.'
      },
      { 
        id: 'educational-interests', 
        title: 'Educational Interests',
        explainerText: 'Is there any educational opportunities that the individual may be interested in attending or learning about? This does not have to be formal and could be something the team could potentially help them with.'
      },
      { 
        id: 'educational-needs', 
        title: 'Educational Needs',
        explainerText: 'What does the individual need from supports to engage in educational activities? Do they self-generate different activities? Do they make their own plans, and get themselves where they need to go? If not what do they need from support people?'
      },
      { 
        id: 'transportation', 
        title: 'Transportation',
        explainerText: 'How does the individual get where they need or want to go? Will they walk? Ride a bike? Take the bus? Ride in a support\'s vehicle? Take Para-transit Etc. What is their level of independence and are there any things that need to be considered when planning transportation such as needing to sit in a particular spot in the car, distance from other vehicles when parking, peak bus times, etc. Are there any other concerns around transportation that need to be listed here?'
      },
      { 
        id: 'money-budgeting', 
        title: 'Money and Budgeting',
        explainerText: 'What is the person\'s financial situation? Where does their money come from? Do they have a financial trustee? Do they understand the concept of money? Could they make transactions on their own? Do they understand the cost of various items? How does their money work and what role do support people play in assisting them and accounting for their money? Do they carry their own money? Do support people pay for things and get reimbursed? Is there a limit on how much money the person should carry at once? Is the person able to save or do they like to spend all their money as soon as they get it?'
      }
    ]
  },
  {
    id: 'crisis-prevention',
    title: 'Crisis Prevention and Intervention Plan',
    subsections: [
      { 
        id: 'environmental-ecological', 
        title: 'Environmental/Ecological',
        explainerText: 'What are the environmental strategies employed by {{ORG}} and the team to reduce the likelihood of challenging, dangerous or harmful behaviors? I.e. location of house, accessible layout of home, lines of sight, locking away sharps or dangerous items, MDF on walls, lexan windows, locations of bedrooms, designated safe/quiet spaces, chimes on doors, solid core doors with metal frames, minimalist environments etc.'
      },
      { 
        id: 'focused-support', 
        title: 'Focused Support',
        explainerText: 'What are the strategies that are in place in order to help the individual stay at their personal best and avoid being in situations that could lead to them getting triggered or escalated? Are there any consistent approaches in place that if not followed could lead to challenging or harmful situations? I.e. incorporating sensory activities, suicide safety protocols from SAFETALKASSIST, Mandt releases or escorts, proactive strategies, reactive strategies, mobile crisis or missing persons reporting, approved restrictive procedures, environmental sweeps, maintaining line of sight supervision, vehicle protocols, acting as a role model/positive influence etc.'
      },
      { 
        id: 'individualized-crisis-cycle', 
        title: 'Individualized Crisis Cycle',
        explainerText: 'This section outlines the specific phases of crisis behavior and appropriate responses for this individual.'
      }
    ]
  }
];


// Section guidance is authored with an {{ORG}} placeholder so the same copy
// serves every client; the deployment's brand fills it in at render time.
const _brandText = (text) => (text || '').replace(/\{\{ORG\}\}/g, orgName());


// The Individualized Crisis Cycle, phase by phase: the questions the form asks
// and the standard support response printed under each. One source for both
// the form and AI Assist, so what the AI is told can't drift from what staff
// see. responseGuidance(name) fills in the person's name.
const CRISIS_PHASES = [
  {
    title: "Phase – Baseline/Personal Best",
    field: "phaseBaseline",
    prompt: "What does the individual look like when they are doing well? What do they say, what do they do? How do you know they are doing well and in a good space? List all of the activities that they enjoy at their baseline phase, these are the options that supports will pull from when the individual is at higher phases of their crisis cycle.",
    placeholder: "Enter baseline phase description...",
    emptyText: "No description provided",
    responseTitle: "Support Response: Keep doing what You're Doing",
    responseField: "supportResponseBaseline",
    responseGuidance: (name) => `Use these moments to continue building your relationship with ${name} and to learn/understand what activities they enjoy at their baseline phase. Treat ${name} with an attitude of respect, nurturing and assurance. Engage with ${name} if this is what is working well and continue to provide your undivided attention.`,
    responsePlaceholder: "Enter additional support response details...",
    responseEmptyText: "No additional response details",
  },
  {
    title: "Phase – Stimulation or Trigger",
    field: "phaseStimulation",
    prompt: "What does the person look like when they are triggered? Is there any noticeable change in their appearance? What do they say and/or do? What are the most common things that serve as triggers for this individual?",
    placeholder: "Enter stimulation/trigger phase description...",
    emptyText: "No description provided",
    responseTitle: "Support Response: Removal of or From Trigger or Stimuli",
    responseField: "supportResponseStimulation",
    responseGuidance: (name) => `Stop and listen in order to identify what is serving as the trigger. If possible, identify and remove the trigger from ${name}. If this is not possible, attempt to remove ${name} from the stimulus or trigger.`,
    responsePlaceholder: "Enter additional support response details...",
    responseEmptyText: "No additional response details",
  },
  {
    title: "Phase – Escalation",
    field: "phaseEscalation",
    prompt: "When the individual moves to their escalation phase what does this look like? What do they say and do? Is there anything occurring at this phase such as physical or verbal threats? Yelling? Swearing? Property destruction? Eloping? Etc.",
    placeholder: "Enter escalation phase description...",
    emptyText: "No description provided",
    responseTitle: "Support Response: Offer Options and Set Expectations (if necessary)",
    responseField: "supportResponseEscalation",
    responseGuidance: (name) => `Your response here is to engage and offer ${name} preferred options from their baseline phase. State what options you would present and how you would go about offering them.`,
    responsePlaceholder: "Enter support response details...",
    responseEmptyText: "No response details provided",
  },
  {
    title: "Phase – Crisis",
    field: "phaseCrisis",
    prompt: "What does the individual look like at this phase? It may be some of the same actions from the escalation phase, but at a higher intensity. Are they engaging in things such as physical aggression towards others or themselves? Major property destruction? Self-harm? Putting themselves or someone else in dangerous situations? Something that might warrant a police or emergency response?",
    placeholder: "Enter crisis phase description...",
    emptyText: "No description provided",
    responseTitle: "Support Response: Least Amount of Interaction Necessary for Safety",
    responseField: "supportResponseCrisis",
    responseGuidance: (name) => `Supports should keep themselves safe and at a distance, while still remaining in the general vicinity of the individual. This allows the individual to know your there for him/her once he/she is ready. At this phase of the individual crisis cycle you want to be doing as much as you possibly can to protect, while keeping everyone safe. Use your judgement to determine how much you should be engaged with the individual. If the individual is involved in physical altercation with a roommate you should attempt to get in between them or separate them if possible. If you feel you are not able to help the situation and danger is present you may need to call for an emergency response (i.e. police).`,
    responsePlaceholder: "Enter additional support response details...",
    responseEmptyText: "No additional response details",
  },
  {
    title: "Phase – De-Escalation Phase",
    field: "phaseDeEscalation",
    prompt: "What does the individual do, look like and say at this phase? How do you know they are beginning to de-escalate? What do you see and hear?",
    placeholder: "Enter de-escalation phase description...",
    emptyText: "No description provided",
    responseTitle: "Support Response: Structured Cooling Off",
    responseField: "supportResponseDeEscalation",
    responseGuidance: (name) => `What are the preferred options from the baseline phase that you are presenting here? What is the best way to engage and help the individual structure their cooling off period?`,
    responsePlaceholder: "Enter support response details...",
    responseEmptyText: "No response details provided",
  },
  {
    title: "Phase – Stabilization",
    field: "phaseStabilization",
    prompt: "What does the individual do, look like and say at this phase? How do you know they are beginning to stabilize? What do you see and hear?",
    placeholder: "Enter stabilization phase description...",
    emptyText: "No description provided",
    responseTitle: "Support Response: Active Listening",
    responseField: "supportResponseStabilization",
    responseGuidance: (name) => `Supports should be fully engaged and listen intently. Supports should sit with open body language and speak softly. Showing individual you care and are there for them both the good and bad is important for them to know. Your goal at this point is to continue helping ${name} to get back to their personal best. If ${name} wants to talk about the interaction, do so, but make sure to keep things positive. You should ask ${name} questions such as what upset him/her and talk about how you can both learn from the situation and do things differently next time. Don't talk to ${name} about consequences or disappointment at this time as it could re-trigger ${name}.`,
    responsePlaceholder: "Enter additional support response details...",
    responseEmptyText: "No additional response details",
  },
  {
    title: "Phase – Post Crisis Phase",
    field: "phasePostCrisis",
    prompt: "What does the individual look like at this phase? What will they say and do? Are they generally tired or low energy? Or do they seem to snap back to their normal self? Do they like to take time alone and perhaps lay down and relax or take a nap?",
    placeholder: "Enter post crisis phase description...",
    emptyText: "No description provided",
    responseTitle: "Support Response: Observation and Support",
    responseField: "supportResponsePostCrisis",
    responseGuidance: (name) => `Provide support and let ${name} know that they are a good person. Encourage ${name} with whatever behaviour is good and provide relaxing options. If ${name} has made a mistake, be sure to keep an eye on him/her to ensure he/she is continuing to be safe. It is important that once minds run through the crisis that supports have a teaching moment with him/her. This should be done once ${name} is back to his/her personal best and this could even be the next day. During this conversation supports can talk to ${name} about possibly apologizing to anyone who has been affected and also talk to ${name} about accountability for his/her actions i.e. paying for any damages he/she may have caused or other restorative practice opportunities.`,
    responsePlaceholder: "Enter additional support response details...",
    responseEmptyText: "No additional response details",
  },
];

// Vision / Hearing / Seizures: the question each comments box answers.
const VISION_HEARING_PROMPTS = {
  vision: "Does the individual wear glasses or contacts? What support do they need to ensure that their glasses/contacts are taken care of and clean? Anything else regarding their vision that may be applicable and has not been mentioned prior.",
  hearing: "Anything related to the individual's hearing that might be important such as has tubes, or frequently has wax build up and what if anything supports need to do about it.",
  seizures: "Additional information about seizures, triggers, management protocols, emergency procedures, medications, and precautions.",
};

const _phaseName = (phase) => phase.title.replace(/^Phase\s*[–-]\s*/, '');

// What AI Assist is told about one crisis-cycle field: where it sits in the
// cycle and the exact question (or standard response) the plan gives for it.
function crisisFieldGuidance(phase, part, name, phaseNotes) {
  const who = name || 'the individual';
  const overview =
    `This is one field of ${who}'s Individualized Crisis Cycle. The cycle describes how ${who} moves ` +
    `through seven phases (${CRISIS_PHASES.map(_phaseName).join(', ')}) and how supports respond at ` +
    'each, so every support recognises the phase and responds the same way.';
  if (part === 'phase') {
    return `${overview}\n\nThis field describes what ${who} looks like, says and does in the ` +
      `"${_phaseName(phase)}" phase. It should answer, using only what the notes say:\n${phase.prompt}`;
  }
  return `${overview}\n\nThis field is the support response for the "${_phaseName(phase)}" phase - ` +
    `${phase.responseTitle.replace(/^Support Response:\s*/, '')}. It says what supports should do for ${who} ` +
    'at this phase. It is shown in the plan in place of the standard response below.\n\n' +
    `The plan's standard response for this phase (context):\n${phase.responseGuidance(who)}` +
    (phaseNotes && phaseNotes.trim()
      ? `\n\nWhat the team wrote about ${who} at this phase (context only):\n${phaseNotes.trim()}`
      : '');
}

// The section editor's AI output is markdown; these fields display as plain
// text, so anything that slips through is unwrapped.
const _plain = (text) => (text || '')
  .replace(/\*\*(.+?)\*\*/g, '$1').replace(/__(.+?)__/g, '$1').replace(/^#{1,6}\s*/gm, '').trim();

// AI Assist for one free-text field inside a structured section. Same endpoint
// and rules as the section editor's, but it only suggests: nothing changes
// until the person presses "Use this" (and then Save).
function FieldAiAssist({ title, guidance, value, onAccept }) {
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [error, setError] = useState('');
  const tooShort = (value || '').trim().length < 20;

  const run = async () => {
    setBusy(true);
    setError('');
    setSuggestion('');
    try {
      const response = await apiService.aiAssist(title, value, guidance, { plainText: true });
      if (response && response.success && response.enhanced_content) {
        setSuggestion(_plain(response.enhanced_content));
      } else {
        setError('AI assistance failed. Please try again.');
      }
    } catch (err) {
      console.error('AI assist error:', err);
      setError('AI assistance failed. Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="field-ai-assist">
      <button
        type="button"
        className={`ai-assist-btn small ${tooShort ? 'disabled' : ''}`}
        onClick={run}
        disabled={busy || tooShort}
        title={tooShort ? 'Write a few notes first, then AI can tidy them up' : 'Tidy up these notes using this field\'s guidance'}
      >
        {busy ? '✨ Working...' : '✨ AI Assist'}
      </button>
      {error && <div className="field-ai-error">{error}</div>}
      {suggestion && (
        <div className="field-ai-preview">
          <div className="field-ai-preview-label">AI suggestion</div>
          <div className="field-ai-preview-text">{suggestion}</div>
          <div className="field-ai-preview-actions">
            <button type="button" className="field-ai-use" onClick={() => { onAccept(suggestion); setSuggestion(''); }}>
              Use this
            </button>
            <button type="button" className="field-ai-dismiss" onClick={() => setSuggestion('')}>
              Keep mine
            </button>
            <span className="field-ai-hint">Then Save the section to keep it.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PatientCarePlan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('one-page-profile');
  const [editingSection, setEditingSection] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [patient, setPatient] = useState(null);
  const [sectionContents, setSectionContents] = useState({});
  const [editingContent, setEditingContent] = useState('');
  const [expandedExplainers, setExpandedExplainers] = useState(() => {
    // Initialize with sections that should be highlighted by default
    const defaultExpanded = {};
    carePlanSections.forEach(section => {
      if (section.subsections) {
        section.subsections.forEach(subsection => {
          if (subsection.highlightByDefault) {
            defaultExpanded[`${section.id}-${subsection.id}`] = true;
          }
        });
      }
    });
    return defaultExpanded;
  }); // Track which explainers are expanded
  
  // Add state for medical contact information
  const [medicalContactData, setMedicalContactData] = useState({
    basicInfo: {
      name: '',
      address: '',
      postalCode: '',
      telephone: '',
      birthDate: '',
      healthNumber: '',
      socialInsurance: ''
    },
    supportDates: {
      homeSupportsStart: '',
      vocationalProvider: '',
      vocationalStart: ''
    },
    emergencyContacts: [
      { name: '', role: '', phone: '' },
      { name: '', role: '', phone: '' },
      { name: '', role: '', phone: '' },
      { name: '', role: '', phone: '' },
      { name: '', role: '', phone: '' },
      { name: '', role: '', phone: '' }
    ],
    medicalPractitioners: {
      generalPractitioner: { name: '', address: '', city: '', telephone: '' },
      optometrist: { name: '', address: '', city: '', telephone: '' },
      dentist: { name: '', address: '', city: '', telephone: '' },
      pharmacy: { name: '', address: '', city: '', telephone: '' },
      psychiatrist: { name: '', address: '', telephone: '' },
      other: ''
    },
    allergies: '',
    medicalAlert: ''
  });

  // Add state for vision, hearing, and seizures data
  const [visionHearingData, setVisionHearingData] = useState({
    vision: {
      eyesightGood: false,
      shortSighted: false,
      longSighted: false,
      noDepthPerception: false,
      seeOnlyShadows: false,
      colorBlind: false,
      legallyBlind: false,
      lazyEye: false,
      comments: ''
    },
    hearing: {
      hearingGood: false,
      mildlyImpaired: false,
      impaired: false,
      deaf: false,
      comments: ''
    },
    seizures: {
      none: false,
      controlledByMedication: false,
      occasional: false,
      weekly: false,
      daily: false,
      comments: ''
    }
  });

  // Add state for crisis cycle data
  const [crisisCycleData, setCrisisCycleData] = useState({
    phaseBaseline: '',
    supportResponseBaseline: '',
    phaseStimulation: '',
    supportResponseStimulation: '',
    phaseEscalation: '',
    supportResponseEscalation: '',
    phaseCrisis: '',
    supportResponseCrisis: '',
    phaseDeEscalation: '',
    supportResponseDeEscalation: '',
    phaseStabilization: '',
    supportResponseStabilization: '',
    phasePostCrisis: '',
    supportResponsePostCrisis: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const mainContentRef = useRef(null);
  
  // AI Assist state
  const [aiAssisting, setAiAssisting] = useState(false);
  const [aiEnhancedContent, setAiEnhancedContent] = useState('');
  const [showAiPreview, setShowAiPreview] = useState(false);
  const [originalContentBeforeAi, setOriginalContentBeforeAi] = useState('');
  const [contentChangedSinceAi, setContentChangedSinceAi] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (id) {
      loadPatientData();
    }
  }, [id]);

  const loadPatientData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load patient information (use appropriate API method based on route)
      const isIndividualRoute = location.pathname.startsWith('/individual/');
      const patientData = isIndividualRoute
        ? await apiService.getIndividual(id)
        : await apiService.getPatient(id);
      setPatient({
        id: patientData.id,
        name: patientData.name,
        age: patientData.age,
        patient_id: patientData.patient_id,
        status: patientData.status,
      });

      // Load all care plan sections for this patient
      await loadAllSections();

    } catch (err) {
      console.error('Failed to load patient data:', err);
      setError('Failed to load patient data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update the individual's status flag (Active / Inactive / Follow-up Required).
  // Persisted immediately + recorded in the audit trail on the backend.
  const handleStatusChange = async (newStatus) => {
    if (!patient || newStatus === patient.status) return;
    const prev = patient.status;
    setPatient((p) => ({ ...p, status: newStatus }));
    setStatusSaving(true);
    try {
      await apiService.updateIndividual(patient.id, { status: newStatus });
    } catch (err) {
      setPatient((p) => ({ ...p, status: prev })); // revert on failure
      setError('Failed to update status. Please try again.');
    } finally {
      setStatusSaving(false);
    }
  };

  const loadAllSections = async () => {
    try {
      const contents = {};

      // Single optimized API call to load ALL care plan sections
      console.log('Loading all care plan sections with optimized query...');
      const startTime = Date.now();

      // Use appropriate API method based on route for care plan sections
      const allSections = await apiService.getPatientCarePlan(id);
      console.log(`Loaded ${allSections.length} sections in ${Date.now() - startTime}ms`);

      // Process all sections and build contents object
      for (const section of allSections) {
        const key = section.subsection_id
          ? `${section.section_id}-${section.subsection_id}`
          : section.section_id;

        contents[key] = section.content || '';
      }

      // Load structured data sections in parallel (medical contacts, vision/hearing, crisis cycle)
      const structuredDataPromises = [];

      // Medical contact information
      structuredDataPromises.push(
        apiService.getCarePlanSection(id, 'contact-information')
          .then(response => {
            if (response.content) {
              try {
                const parsedData = JSON.parse(response.content);
                setMedicalContactData(parsedData);
              } catch (parseError) {
                console.log('Failed to parse medical contact data, using defaults');
              }
            }
          })
          .catch(err => console.log('No existing medical contact data found'))
      );

      // Vision/hearing data
      structuredDataPromises.push(
        apiService.getCarePlanSection(id, 'health-wellness', 'vision-hearing')
          .then(response => {
            if (response.content) {
              try {
                const parsedData = JSON.parse(response.content);
                setVisionHearingData(parsedData);
              } catch (parseError) {
                console.log('Failed to parse vision-hearing data, using defaults');
              }
            }
          })
          .catch(err => console.log('No existing vision-hearing data found'))
      );

      // Crisis cycle data
      structuredDataPromises.push(
        apiService.getCarePlanSection(id, 'crisis-prevention', 'individualized-crisis-cycle')
          .then(response => {
            if (response.content) {
              try {
                const parsedData = JSON.parse(response.content);
                setCrisisCycleData(parsedData);
              } catch (parseError) {
                console.log('Failed to parse crisis cycle data, using defaults');
              }
            }
          })
          .catch(err => console.log('No existing crisis cycle data found'))
      );

      // Wait for all structured data to load in parallel
      await Promise.all(structuredDataPromises);

      setSectionContents(contents);
    } catch (err) {
      console.error('Failed to load section contents:', err);
      // Fallback to old method if optimized query fails
      console.log('Falling back to individual section loading...');
      await loadAllSectionsFallback();
    }
  };

  // Fallback method for loading sections individually (used if optimized query fails)
  const loadAllSectionsFallback = async () => {
    try {
      const contents = {};

      // Load each section and subsection individually (original method)
      for (const section of carePlanSections) {
        if (section.id === 'contact-information') {
          // Load medical contact information as structured data
          try {
            const response = await apiService.getCarePlanSection(id, section.id);
            if (response.content) {
              try {
                const parsedData = JSON.parse(response.content);
                setMedicalContactData(parsedData);
              } catch (parseError) {
                console.log('Failed to parse medical contact data, using defaults');
              }
            }
          } catch (err) {
            console.log('No existing medical contact data found');
          }
        } else if (section.id === 'health-wellness' && section.subsections.find(sub => sub.id === 'vision-hearing')) {
          // Load vision-hearing data as structured data
          try {
            const response = await apiService.getCarePlanSection(id, section.id, 'vision-hearing');
            if (response.content) {
              try {
                const parsedData = JSON.parse(response.content);
                setVisionHearingData(parsedData);
              } catch (parseError) {
                console.log('Failed to parse vision-hearing data, using defaults');
              }
            }
          } catch (err) {
            console.log('No existing vision-hearing data found');
          }

          // Load other subsections for health-wellness section normally
          for (const subsection of section.subsections) {
            if (subsection.id !== 'vision-hearing') {
              try {
                const response = await apiService.getCarePlanSection(id, section.id, subsection.id);
                contents[`${section.id}-${subsection.id}`] = response.content || '';
              } catch (err) {
                contents[`${section.id}-${subsection.id}`] = '';
              }
            }
          }
        } else if (section.id === 'crisis-prevention' && section.subsections.find(sub => sub.id === 'individualized-crisis-cycle')) {
          // Load crisis cycle data as structured data
          try {
            const response = await apiService.getCarePlanSection(id, section.id, 'individualized-crisis-cycle');
            if (response.content) {
              try {
                const parsedData = JSON.parse(response.content);
                setCrisisCycleData(parsedData);
              } catch (parseError) {
                console.log('Failed to parse crisis cycle data, using defaults');
              }
            }
          } catch (err) {
            console.log('No existing crisis cycle data found');
          }

          // Load other subsections for crisis-prevention section normally
          for (const subsection of section.subsections) {
            if (subsection.id !== 'individualized-crisis-cycle') {
              try {
                const response = await apiService.getCarePlanSection(id, section.id, subsection.id);
                contents[`${section.id}-${subsection.id}`] = response.content || '';
              } catch (err) {
                contents[`${section.id}-${subsection.id}`] = '';
              }
            }
          }
        } else if (section.subsections.length === 0) {
          // Load main section content
          try {
            const response = await apiService.getCarePlanSection(id, section.id);
            contents[section.id] = response.content || '';
          } catch (err) {
            contents[section.id] = '';
          }
        } else {
          // Load subsection contents
          for (const subsection of section.subsections) {
            try {
              const response = await apiService.getCarePlanSection(id, section.id, subsection.id);
              contents[`${section.id}-${subsection.id}`] = response.content || '';
            } catch (err) {
              contents[`${section.id}-${subsection.id}`] = '';
            }
          }
        }
      }

      setSectionContents(contents);
    } catch (err) {
      console.error('Failed to load section contents with fallback method:', err);
    }
  };

  const startEditing = (sectionId, subsectionId = null) => {
    const key = subsectionId ? `${sectionId}-${subsectionId}` : sectionId;
    setEditingSection(key);
    setEditingContent(sectionContents[key] || '');
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setEditingContent('');
    setShowAiPreview(false);
    setAiEnhancedContent('');
    setOriginalContentBeforeAi('');
    setContentChangedSinceAi(false);
    setShowSaveReminder(false);
  };

  const handleAiAssist = async (sectionId, subsectionId = null) => {
    try {
      setAiAssisting(true);
      
      // Get section and subsection data
      const sectionData = carePlanSections.find(s => s.id === sectionId);
      const subsectionData = subsectionId ?
        sectionData.subsections.find(sub => sub.id === subsectionId) : null;
      
      // Get the title and guidelines
      const title = subsectionData ? subsectionData.title : sectionData.title;
      const guidelines = subsectionData ? 
        _brandText(subsectionData.explainerText || '') : 
        _brandText(sectionData.explainerText || '');
      
      // Store original content before AI assist
      setOriginalContentBeforeAi(editingContent);
      
      // Call AI assist API
      const response = await apiService.aiAssist(title, editingContent, guidelines);
      
      if (response.success) {
        setAiEnhancedContent(response.enhanced_content);
        setShowAiPreview(true);
        setContentChangedSinceAi(false);
      } else {
        alert('AI assistance failed. Please try again.');
      }
      
    } catch (err) {
      console.error('AI assist error:', err);
      alert('AI assistance failed. Please check your connection and try again.');
    } finally {
      setAiAssisting(false);
    }
  };

  const [showSaveReminder, setShowSaveReminder] = useState(false);

  const acceptAiContent = () => {
    setEditingContent(aiEnhancedContent);
    setShowAiPreview(false);
    setContentChangedSinceAi(false);
    setShowSaveReminder(true);
    setAiEnhancedContent('');
    setOriginalContentBeforeAi('');
    setEditorKey(prev => prev + 1); // Force editor to re-render with new content
  };

  const rejectAiContent = () => {
    setShowAiPreview(false);
    setAiEnhancedContent('');
    setOriginalContentBeforeAi('');
  };

  const handleContentChange = (newContent) => {
    setEditingContent(newContent);
    if (showAiPreview || aiEnhancedContent) {
      setContentChangedSinceAi(newContent !== originalContentBeforeAi);
    }
  };

  const saveSection = async (sectionId, subsectionId = null) => {
    try {
      setSaving(true);

      const sectionData = carePlanSections.find(s => s.id === sectionId);
      const subsectionData = subsectionId ?
        sectionData.subsections.find(sub => sub.id === subsectionId) : null;

      await apiService.updateCarePlanSection(
        id,
        sectionId,
        editingContent,
        subsectionId,
        sectionData.title,
        subsectionData?.title || ''
      );

      // Update local state
      const key = subsectionId ? `${sectionId}-${subsectionId}` : sectionId;
      setSectionContents(prev => ({
        ...prev,
        [key]: editingContent
      }));

      setEditingSection(null);
      setEditingContent('');
      setShowSaveReminder(false);

      // Show success feedback
      console.log('Section saved successfully!');

    } catch (err) {
      console.error('Failed to save section:', err);
      alert('Failed to save section. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Optional batch save functionality for saving multiple sections at once
  const saveMultipleSections = async (sectionsToSave) => {
    try {
      setSaving(true);

      const batchData = sectionsToSave.map(({ sectionId, subsectionId, content }) => {
        const sectionData = carePlanSections.find(s => s.id === sectionId);
        const subsectionData = subsectionId ?
          sectionData.subsections.find(sub => sub.id === subsectionId) : null;

        return {
          section_id: sectionId,
          subsection_id: subsectionId,
          content: content,
          section_title: sectionData.title,
          subsection_title: subsectionData?.title || ''
        };
      });

      const result = await apiService.batchUpdateCarePlanSections(id, batchData);

      // Update local state for all saved sections
      const updatedContents = { ...sectionContents };
      sectionsToSave.forEach(({ sectionId, subsectionId, content }) => {
        const key = subsectionId ? `${sectionId}-${subsectionId}` : sectionId;
        updatedContents[key] = content;
      });
      setSectionContents(updatedContents);

      console.log(`Batch saved ${result.updated_sections.length} sections successfully!`);
      return result;

    } catch (err) {
      console.error('Failed to batch save sections:', err);
      alert('Failed to save sections. Please try again.');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const saveMedicalContactSection = async () => {
    try {
      setSaving(true);
      
      const jsonContent = JSON.stringify(medicalContactData);
      
      await apiService.updateCarePlanSection(
        id,
        'contact-information',
        jsonContent,
        null,
        'Personal/Medical Contact Information',
        ''
      );

      setEditingSection(null);
      
      // Show success feedback
      console.log('Medical contact information saved successfully!');
      
    } catch (err) {
      console.error('Failed to save medical contact information:', err);
      alert('Failed to save medical contact information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateMedicalContactField = (section, field, value, index = null, practitionerType = null) => {
    setMedicalContactData(prev => {
      const newData = { ...prev };
      if (index !== null) {
        newData[section][index][field] = value;
      } else if (practitionerType) {
        newData[section][practitionerType][field] = value;
      } else if (typeof newData[section] === 'object' && !Array.isArray(newData[section])) {
        newData[section][field] = value;
      } else {
        newData[section] = value;
      }
      return newData;
    });
  };

  const updateVisionHearingField = (section, field, value) => {
    setVisionHearingData(prev => {
      const newData = { ...prev };
      if (field === 'comments') {
        newData[section][field] = value;
      } else {
        // Handle checkbox logic - uncheck others when one is checked for certain groups
        if (section === 'vision' && field !== 'comments') {
          // For vision, allow multiple selections
          newData[section][field] = value;
        } else if (section === 'hearing' && field !== 'comments') {
          // For hearing, only one option should be selected at a time
          Object.keys(newData[section]).forEach(key => {
            if (key !== 'comments') {
              newData[section][key] = false;
            }
          });
          newData[section][field] = value;
        } else if (section === 'seizures' && field !== 'comments') {
          // For seizures, only one option should be selected at a time
          Object.keys(newData[section]).forEach(key => {
            if (key !== 'comments') {
              newData[section][key] = false;
            }
          });
          newData[section][field] = value;
        }
      }
      return newData;
    });
  };

  const saveVisionHearingSection = async () => {
    try {
      setSaving(true);
      
      const jsonContent = JSON.stringify(visionHearingData);
      
      await apiService.updateCarePlanSection(
        id,
        'health-wellness',
        jsonContent,
        'vision-hearing',
        'Health & Wellness',
        'Vision, Hearing'
      );

      setEditingSection(null);
      
      // Show success feedback
      console.log('Vision, hearing, and seizures information saved successfully!');
      
    } catch (err) {
      console.error('Failed to save vision, hearing, and seizures information:', err);
      alert('Failed to save vision, hearing, and seizures information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateCrisisCycleField = (field, value) => {
    setCrisisCycleData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      
      // Generate the PDF blob
      const pdfBlob = await generateCarePlanPDF(
        patient,
        sectionContents,
        medicalContactData,
        visionHearingData,
        crisisCycleData
      );
      
      // Create a download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${patient.name.replace(/\s+/g, '_')}_Care_Plan_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('PDF exported successfully!');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const saveCrisisCycleSection = async () => {
    try {
      setSaving(true);
      
      const jsonContent = JSON.stringify(crisisCycleData);
      
      await apiService.updateCarePlanSection(
        id,
        'crisis-prevention',
        jsonContent,
        'individualized-crisis-cycle',
        'Crisis Prevention and Intervention Plan',
        'Individualized Crisis Cycle'
      );

      setEditingSection(null);
      
      // Show success feedback
      console.log('Crisis cycle information saved successfully!');
      
    } catch (err) {
      console.error('Failed to save crisis cycle information:', err);
      alert('Failed to save crisis cycle information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleExplainer = (sectionKey) => {
    setExpandedExplainers(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const getExplainerText = (section, subsection = null) => {
    if (subsection) {
      return _brandText(subsection.explainerText || '');
    }
    return _brandText(section.explainerText || '');
  };

  const renderCrisisCycleSection = () => (
    <div key="individualized-crisis-cycle" id="individualized-crisis-cycle" className="care-section crisis-cycle-section">
      <div className="section-header">
        <h2>Individualized Crisis Cycle</h2>
        <div className="section-actions">
          {editingSection === 'individualized-crisis-cycle' ? (
            <div className="edit-actions">
              <button 
                className="save-btn" 
                onClick={saveCrisisCycleSection}
                disabled={saving}
              >
                {saving ? '💾 Saving...' : '💾 Save'}
              </button>
              <button className="cancel-btn" onClick={cancelEditing}>
                ❌ Cancel
              </button>
            </div>
          ) : (
            <button 
              className="edit-btn" 
              onClick={() => setEditingSection('individualized-crisis-cycle')}
            >
              ✏️ Edit
            </button>
          )}
        </div>
      </div>
      
      <div className="crisis-cycle-content">
        {CRISIS_PHASES.map((phase) => (
          <div className="crisis-phase" key={phase.field}>
            <h3>{phase.title}</h3>
            <div className="phase-description">
              <strong>{phase.prompt}</strong>
            </div>
            {editingSection === 'individualized-crisis-cycle' ? (
              <>
                <textarea
                  value={crisisCycleData[phase.field]}
                  onChange={(e) => updateCrisisCycleField(phase.field, e.target.value)}
                  placeholder={phase.placeholder}
                  rows="4"
                />
                <FieldAiAssist
                  title={`Individualized Crisis Cycle: ${phase.title}`}
                  guidance={crisisFieldGuidance(phase, 'phase', patient?.name)}
                  value={crisisCycleData[phase.field]}
                  onAccept={(text) => updateCrisisCycleField(phase.field, text)}
                />
              </>
            ) : (
              <div className="phase-content">
                {crisisCycleData[phase.field] || phase.emptyText}
              </div>
            )}

            <div className="support-response">
              <strong>{phase.responseTitle}</strong>
              {!crisisCycleData[phase.responseField] && (
              <div className="response-description">
                {phase.responseGuidance(patient?.name || '[Patient Name]')}
              </div>
              )}
              {editingSection === 'individualized-crisis-cycle' ? (
                <>
                  <textarea
                    value={crisisCycleData[phase.responseField]}
                    onChange={(e) => updateCrisisCycleField(phase.responseField, e.target.value)}
                    placeholder={phase.responsePlaceholder}
                    rows="3"
                  />
                  <FieldAiAssist
                    title={`Individualized Crisis Cycle: ${phase.title} - ${phase.responseTitle}`}
                    guidance={crisisFieldGuidance(phase, 'response', patient?.name, crisisCycleData[phase.field])}
                    value={crisisCycleData[phase.responseField]}
                    onAccept={(text) => updateCrisisCycleField(phase.responseField, text)}
                  />
                </>
              ) : (
                <div className="phase-content">
                  {crisisCycleData[phase.responseField] || phase.responseEmptyText}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderVisionHearingSection = () => (
    <div key="vision-hearing" id="vision-hearing" className="care-section vision-hearing-section">
      <div className="section-header">
        <h2>Vision, Hearing, Seizures</h2>
        <div className="section-actions">
          {editingSection === 'vision-hearing' ? (
            <div className="edit-actions">
              <button 
                className="save-btn" 
                onClick={saveVisionHearingSection}
                disabled={saving}
              >
                {saving ? '💾 Saving...' : '💾 Save'}
              </button>
              <button className="cancel-btn" onClick={cancelEditing}>
                ❌ Cancel
              </button>
            </div>
          ) : (
            <button 
              className="edit-btn" 
              onClick={() => setEditingSection('vision-hearing')}
            >
              ✏️ Edit
            </button>
          )}
        </div>
      </div>
      
      <div className="vision-hearing-content">
        {/* Vision Section */}
        <div className="vh-subsection vision-subsection">
          <h3>Vision</h3>
          <div className="checkbox-intro">
            <span className="patient-name-placeholder">{patient?.name || '[Patient Name]'} - Check any/all boxes that apply</span>
          </div>
          
          <div className="checkbox-grid">
            <div className="checkbox-row">
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.vision.eyesightGood}
                  onChange={(e) => updateVisionHearingField('vision', 'eyesightGood', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                eyesight is good
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.vision.shortSighted}
                  onChange={(e) => updateVisionHearingField('vision', 'shortSighted', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                short sighted
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.vision.longSighted}
                  onChange={(e) => updateVisionHearingField('vision', 'longSighted', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                long sighted
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.vision.noDepthPerception}
                  onChange={(e) => updateVisionHearingField('vision', 'noDepthPerception', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                no depth perception
              </label>
            </div>
            <div className="checkbox-row">
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.vision.seeOnlyShadows}
                  onChange={(e) => updateVisionHearingField('vision', 'seeOnlyShadows', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                see only shadows
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.vision.colorBlind}
                  onChange={(e) => updateVisionHearingField('vision', 'colorBlind', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                color blind
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.vision.legallyBlind}
                  onChange={(e) => updateVisionHearingField('vision', 'legallyBlind', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                legally blind
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.vision.lazyEye}
                  onChange={(e) => updateVisionHearingField('vision', 'lazyEye', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                lazy eye
              </label>
            </div>
          </div>
          
          <div className="comments-section">
            <label><strong>Comments:</strong> {VISION_HEARING_PROMPTS.vision}</label>
            {editingSection === 'vision-hearing' ? (
              <>
                <textarea 
                  value={visionHearingData.vision.comments}
                  onChange={(e) => updateVisionHearingField('vision', 'comments', e.target.value)}
                  placeholder="Enter vision-related comments..."
                  rows="3"
                />
                <FieldAiAssist
                  title="Health and Wellness: Vision"
                  guidance={`Health and Wellness: Vision comments for ${patient?.name || 'the individual'}. ` +
                    `The checkboxes above record the basics; this comment adds what supports need to know. ` +
                    `It should answer, using only what the notes say: ${VISION_HEARING_PROMPTS.vision}`}
                  value={visionHearingData.vision.comments}
                  onAccept={(text) => updateVisionHearingField('vision', 'comments', text)}
                />
              </>
            ) : (
              <div className="comments-display">
                {visionHearingData.vision.comments || 'No additional comments'}
              </div>
            )}
          </div>
        </div>

        {/* Hearing Section */}
        <div className="vh-subsection hearing-subsection">
          <h3>Hearing</h3>
          <div className="checkbox-intro">
            <span className="patient-name-placeholder">{patient?.name || '[Patient Name]'} - Check any/all boxes that apply</span>
          </div>
          
          <div className="checkbox-grid">
            <div className="checkbox-row">
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.hearing.hearingGood}
                  onChange={(e) => updateVisionHearingField('hearing', 'hearingGood', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                hearing is: good
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.hearing.mildlyImpaired}
                  onChange={(e) => updateVisionHearingField('hearing', 'mildlyImpaired', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                mildly impaired
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.hearing.impaired}
                  onChange={(e) => updateVisionHearingField('hearing', 'impaired', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                impaired
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.hearing.deaf}
                  onChange={(e) => updateVisionHearingField('hearing', 'deaf', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                deaf
              </label>
            </div>
          </div>
          
          <div className="comments-section">
            <label><strong>Comments:</strong> {VISION_HEARING_PROMPTS.hearing}</label>
            {editingSection === 'vision-hearing' ? (
              <>
                <textarea 
                  value={visionHearingData.hearing.comments}
                  onChange={(e) => updateVisionHearingField('hearing', 'comments', e.target.value)}
                  placeholder="Enter hearing-related comments..."
                  rows="3"
                />
                <FieldAiAssist
                  title="Health and Wellness: Hearing"
                  guidance={`Health and Wellness: Hearing comments for ${patient?.name || 'the individual'}. ` +
                    `The checkboxes above record the basics; this comment adds what supports need to know. ` +
                    `It should answer, using only what the notes say: ${VISION_HEARING_PROMPTS.hearing}`}
                  value={visionHearingData.hearing.comments}
                  onAccept={(text) => updateVisionHearingField('hearing', 'comments', text)}
                />
              </>
            ) : (
              <div className="comments-display">
                {visionHearingData.hearing.comments || 'No additional comments'}
              </div>
            )}
          </div>
        </div>

        {/* Seizures Section */}
        <div className="vh-subsection seizures-subsection">
          <h3>Seizures: Check any boxes that apply</h3>
          
          <div className="checkbox-grid">
            <div className="checkbox-row">
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.seizures.none}
                  onChange={(e) => updateVisionHearingField('seizures', 'none', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                None
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.seizures.controlledByMedication}
                  onChange={(e) => updateVisionHearingField('seizures', 'controlledByMedication', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                Controlled by medication
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.seizures.occasional}
                  onChange={(e) => updateVisionHearingField('seizures', 'occasional', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                Occasional
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.seizures.weekly}
                  onChange={(e) => updateVisionHearingField('seizures', 'weekly', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                Weekly
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={visionHearingData.seizures.daily}
                  onChange={(e) => updateVisionHearingField('seizures', 'daily', e.target.checked)}
                  disabled={editingSection !== 'vision-hearing'}
                />
                <span className="checkmark"></span>
                Daily
              </label>
            </div>
          </div>
          
          <div className="comments-section">
            <label><strong>Comments:</strong> {VISION_HEARING_PROMPTS.seizures}</label>
            {editingSection === 'vision-hearing' ? (
              <>
                <textarea 
                  value={visionHearingData.seizures.comments}
                  onChange={(e) => updateVisionHearingField('seizures', 'comments', e.target.value)}
                  placeholder="Enter seizure-related comments..."
                  rows="3"
                />
                <FieldAiAssist
                  title="Health and Wellness: Seizures"
                  guidance={`Health and Wellness: Seizures comments for ${patient?.name || 'the individual'}. ` +
                    `The checkboxes above record the basics; this comment adds what supports need to know. ` +
                    `It should answer, using only what the notes say: ${VISION_HEARING_PROMPTS.seizures}`}
                  value={visionHearingData.seizures.comments}
                  onAccept={(text) => updateVisionHearingField('seizures', 'comments', text)}
                />
              </>
            ) : (
              <div className="comments-display">
                {visionHearingData.seizures.comments || 'No additional comments'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderMedicalContactSection = () => (
    <div key="contact-information" id="contact-information" className="care-section medical-contact-section">
      <div className="section-header">
        <h2>Personal/Medical Contact Information</h2>
        <div className="section-actions">
          {editingSection === 'contact-information' ? (
            <div className="edit-actions">
              <button 
                className="save-btn" 
                onClick={saveMedicalContactSection}
                disabled={saving}
              >
                {saving ? '💾 Saving...' : '💾 Save'}
              </button>
              <button className="cancel-btn" onClick={cancelEditing}>
                ❌ Cancel
              </button>
            </div>
          ) : (
            <button 
              className="edit-btn" 
              onClick={() => setEditingSection('contact-information')}
            >
              ✏️ Edit
            </button>
          )}
        </div>
      </div>
      
      <div className="medical-contact-content">
        <div className="medical-intro">
          <p>This page contains the names, addresses and contact numbers for people to contact in the event of an emergency.</p>
        </div>

        {/* Basic Information and Support Dates */}
        <div className="medical-info-grid">
          <div className="basic-info-section">
            <div className="info-row">
              <label>Name:</label>
              {editingSection === 'contact-information' ? (
                <input 
                  type="text" 
                  value={medicalContactData.basicInfo.name}
                  onChange={(e) => updateMedicalContactField('basicInfo', 'name', e.target.value)}
                />
              ) : (
                <span>{medicalContactData.basicInfo.name || 'Name'}</span>
              )}
            </div>
            <div className="info-row">
              <label>Address:</label>
              {editingSection === 'contact-information' ? (
                <input 
                  type="text" 
                  value={medicalContactData.basicInfo.address}
                  onChange={(e) => updateMedicalContactField('basicInfo', 'address', e.target.value)}
                />
              ) : (
                <span>{medicalContactData.basicInfo.address || 'Address'}</span>
              )}
            </div>
            <div className="info-row">
              <label>Regina, Sk.</label>
              {editingSection === 'contact-information' ? (
                <input 
                  type="text" 
                  placeholder="Postal Code"
                  value={medicalContactData.basicInfo.postalCode}
                  onChange={(e) => updateMedicalContactField('basicInfo', 'postalCode', e.target.value)}
                />
              ) : (
                <span className="postal-code">{medicalContactData.basicInfo.postalCode || 'Postal Code'}</span>
              )}
            </div>
            <div className="info-row">
              <label>Telephone:</label>
              {editingSection === 'contact-information' ? (
                <input 
                  type="text" 
                  value={medicalContactData.basicInfo.telephone}
                  onChange={(e) => updateMedicalContactField('basicInfo', 'telephone', e.target.value)}
                />
              ) : (
                <span>{medicalContactData.basicInfo.telephone || '(306) ###-####'}</span>
              )}
            </div>
            <div className="info-row">
              <label>Birth Date:</label>
              {editingSection === 'contact-information' ? (
                <input 
                  type="text" 
                  value={medicalContactData.basicInfo.birthDate}
                  onChange={(e) => updateMedicalContactField('basicInfo', 'birthDate', e.target.value)}
                />
              ) : (
                <span>{medicalContactData.basicInfo.birthDate || 'Month Day, Year'}</span>
              )}
            </div>
            <div className="info-row">
              <label>SK Health Number:</label>
              {editingSection === 'contact-information' ? (
                <input 
                  type="text" 
                  value={medicalContactData.basicInfo.healthNumber}
                  onChange={(e) => updateMedicalContactField('basicInfo', 'healthNumber', e.target.value)}
                />
              ) : (
                <span>{medicalContactData.basicInfo.healthNumber || '### ### ###'}</span>
              )}
            </div>
            <div className="info-row">
              <label>Social Insurance Number:</label>
              {editingSection === 'contact-information' ? (
                <input 
                  type="text" 
                  value={medicalContactData.basicInfo.socialInsurance}
                  onChange={(e) => updateMedicalContactField('basicInfo', 'socialInsurance', e.target.value)}
                />
              ) : (
                <span>{medicalContactData.basicInfo.socialInsurance || '### ### ###'}</span>
              )}
            </div>
          </div>

          <div className="support-dates-section">
            <div className="info-row">
              <label>Date Home Supports began through {orgName()}:</label>
              {editingSection === 'contact-information' ? (
                <input 
                  type="text" 
                  value={medicalContactData.supportDates.homeSupportsStart}
                  onChange={(e) => updateMedicalContactField('supportDates', 'homeSupportsStart', e.target.value)}
                />
              ) : (
                <span>{medicalContactData.supportDates.homeSupportsStart || 'Month Day, Year'}</span>
              )}
            </div>
            <div className="info-row">
              <label>Vocational/Daytime Support Provider:</label>
              {editingSection === 'contact-information' ? (
                <input 
                  type="text" 
                  value={medicalContactData.supportDates.vocationalProvider}
                  onChange={(e) => updateMedicalContactField('supportDates', 'vocationalProvider', e.target.value)}
                />
              ) : (
                <span>{medicalContactData.supportDates.vocationalProvider || `${orgName()} – Change if this is different`}</span>
              )}
            </div>
            <div className="info-row">
              <label>Date Vocational/Daytime Support began:</label>
              {editingSection === 'contact-information' ? (
                <input 
                  type="text" 
                  value={medicalContactData.supportDates.vocationalStart}
                  onChange={(e) => updateMedicalContactField('supportDates', 'vocationalStart', e.target.value)}
                />
              ) : (
                <span>{medicalContactData.supportDates.vocationalStart || 'Month Day, Year'}</span>
              )}
            </div>
          </div>
        </div>

        {/* Emergency Contact Numbers */}
        <div className="emergency-contacts">
          <h3>Emergency Contact Numbers:</h3>
          <ol>
            {medicalContactData.emergencyContacts.map((contact, index) => (
              <li key={index} className="emergency-contact">
                {editingSection === 'contact-information' ? (
                  <div className="contact-edit-row">
                    <input 
                      type="text" 
                      placeholder="Name"
                      value={contact.name}
                      onChange={(e) => updateMedicalContactField('emergencyContacts', 'name', e.target.value, index)}
                    />
                    <input 
                      type="text" 
                      placeholder="Role"
                      value={contact.role}
                      onChange={(e) => updateMedicalContactField('emergencyContacts', 'role', e.target.value, index)}
                    />
                    <input 
                      type="text" 
                      placeholder="Phone"
                      value={contact.phone}
                      onChange={(e) => updateMedicalContactField('emergencyContacts', 'phone', e.target.value, index)}
                    />
                  </div>
                ) : (
                  <span>
                    {contact.name || 'Name'}, {contact.role || 'Role'} ({contact.phone || '(306) ###-####'})
                    {index === 4 && <span className="clsd-highlight"> CLSD/CFP Community Service Worker</span>}
                  </span>
                )}
              </li>
            ))}
            <li className="add-family-note">
              <em>Add any family members if applicable (i.e. mom/dad/guardian)</em>
            </li>
          </ol>
        </div>

        {/* Medical Practitioners Table */}
        <div className="medical-practitioners">
          <table className="practitioners-table">
            <tbody>
            <tr>
              <td className="practitioner-section">
                <strong>General Practitioner</strong><br />
                                 {editingSection === 'contact-information' ? (
                   <div className="practitioner-edit">
                     <input placeholder="Name:" value={medicalContactData.medicalPractitioners.generalPractitioner.name} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'name', e.target.value, null, 'generalPractitioner')} />
                     <input placeholder="Address:" value={medicalContactData.medicalPractitioners.generalPractitioner.address} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'address', e.target.value, null, 'generalPractitioner')} />
                     <input placeholder="City:" value={medicalContactData.medicalPractitioners.generalPractitioner.city} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'city', e.target.value, null, 'generalPractitioner')} />
                     <input placeholder="Telephone:" value={medicalContactData.medicalPractitioners.generalPractitioner.telephone} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'telephone', e.target.value, null, 'generalPractitioner')} />
                   </div>
                ) : (
                  <div>
                    Name: {medicalContactData.medicalPractitioners.generalPractitioner.name || ''}<br />
                    Address: {medicalContactData.medicalPractitioners.generalPractitioner.address || ''}<br />
                    City: {medicalContactData.medicalPractitioners.generalPractitioner.city || ''}<br />
                    Telephone: {medicalContactData.medicalPractitioners.generalPractitioner.telephone || ''}
                  </div>
                )}
              </td>
              <td className="practitioner-section">
                <strong>Optometrist</strong><br />
                                 {editingSection === 'contact-information' ? (
                   <div className="practitioner-edit">
                     <input placeholder="Name:" value={medicalContactData.medicalPractitioners.optometrist.name} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'name', e.target.value, null, 'optometrist')} />
                     <input placeholder="Address:" value={medicalContactData.medicalPractitioners.optometrist.address} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'address', e.target.value, null, 'optometrist')} />
                     <input placeholder="City:" value={medicalContactData.medicalPractitioners.optometrist.city} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'city', e.target.value, null, 'optometrist')} />
                     <input placeholder="Telephone:" value={medicalContactData.medicalPractitioners.optometrist.telephone} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'telephone', e.target.value, null, 'optometrist')} />
                   </div>
                ) : (
                  <div>
                    Name: {medicalContactData.medicalPractitioners.optometrist.name || ''}<br />
                    Address: {medicalContactData.medicalPractitioners.optometrist.address || ''}<br />
                    City: {medicalContactData.medicalPractitioners.optometrist.city || ''}<br />
                    Telephone: {medicalContactData.medicalPractitioners.optometrist.telephone || ''}
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <td className="practitioner-section">
                <strong>Dentist</strong><br />
                                 {editingSection === 'contact-information' ? (
                   <div className="practitioner-edit">
                     <input placeholder="Name:" value={medicalContactData.medicalPractitioners.dentist.name} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'name', e.target.value, null, 'dentist')} />
                     <input placeholder="Address:" value={medicalContactData.medicalPractitioners.dentist.address} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'address', e.target.value, null, 'dentist')} />
                     <input placeholder="City:" value={medicalContactData.medicalPractitioners.dentist.city} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'city', e.target.value, null, 'dentist')} />
                     <input placeholder="Telephone:" value={medicalContactData.medicalPractitioners.dentist.telephone} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'telephone', e.target.value, null, 'dentist')} />
                   </div>
                ) : (
                  <div>
                    Name: {medicalContactData.medicalPractitioners.dentist.name || ''}<br />
                    Address: {medicalContactData.medicalPractitioners.dentist.address || ''}<br />
                    City: {medicalContactData.medicalPractitioners.dentist.city || ''}<br />
                    Telephone: {medicalContactData.medicalPractitioners.dentist.telephone || ''}
                  </div>
                )}
              </td>
              <td className="practitioner-section">
                <strong>Pharmacy</strong><br />
                                 {editingSection === 'contact-information' ? (
                   <div className="practitioner-edit">
                     <input placeholder="Name:" value={medicalContactData.medicalPractitioners.pharmacy.name} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'name', e.target.value, null, 'pharmacy')} />
                     <input placeholder="Address:" value={medicalContactData.medicalPractitioners.pharmacy.address} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'address', e.target.value, null, 'pharmacy')} />
                     <input placeholder="City:" value={medicalContactData.medicalPractitioners.pharmacy.city} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'city', e.target.value, null, 'pharmacy')} />
                     <input placeholder="Telephone:" value={medicalContactData.medicalPractitioners.pharmacy.telephone} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'telephone', e.target.value, null, 'pharmacy')} />
                   </div>
                ) : (
                  <div>
                    Name: {medicalContactData.medicalPractitioners.pharmacy.name || ''}<br />
                    Address: {medicalContactData.medicalPractitioners.pharmacy.address || ''}<br />
                    City: {medicalContactData.medicalPractitioners.pharmacy.city || ''}<br />
                    Telephone: {medicalContactData.medicalPractitioners.pharmacy.telephone || ''}
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <td className="practitioner-section">
                <strong>Psychiatrist</strong><br />
                                 {editingSection === 'contact-information' ? (
                   <div className="practitioner-edit">
                     <input placeholder="Name:" value={medicalContactData.medicalPractitioners.psychiatrist.name} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'name', e.target.value, null, 'psychiatrist')} />
                     <input placeholder="Address:" value={medicalContactData.medicalPractitioners.psychiatrist.address} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'address', e.target.value, null, 'psychiatrist')} />
                     <input placeholder="Telephone:" value={medicalContactData.medicalPractitioners.psychiatrist.telephone} 
                            onChange={(e) => updateMedicalContactField('medicalPractitioners', 'telephone', e.target.value, null, 'psychiatrist')} />
                   </div>
                ) : (
                  <div>
                    Name: {medicalContactData.medicalPractitioners.psychiatrist.name || ''}<br />
                    Address: {medicalContactData.medicalPractitioners.psychiatrist.address || ''}<br />
                    Telephone: {medicalContactData.medicalPractitioners.psychiatrist.telephone || ''}
                  </div>
                )}
              </td>
              <td className="practitioner-section other-practitioners">
                <strong>Add any other medical practitioners necessary</strong><br />
                {editingSection === 'contact-information' ? (
                  <textarea 
                    value={medicalContactData.medicalPractitioners.other}
                    onChange={(e) => updateMedicalContactField('medicalPractitioners', 'other', e.target.value)}
                    placeholder="Enter additional practitioners..."
                  />
                ) : (
                  <div>{medicalContactData.medicalPractitioners.other || ''}</div>
                )}
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        {/* Allergies and Medical Alert */}
        <div className="medical-alerts">
          <div className="alert-section allergies">
            <strong>Allergies</strong><br />
            {editingSection === 'contact-information' ? (
              <textarea 
                value={medicalContactData.allergies}
                onChange={(e) => updateMedicalContactField('allergies', null, e.target.value)}
                placeholder="List any allergies the individual has. Leave this highlighted to draw attention to it."
              />
            ) : (
              <div>{medicalContactData.allergies || 'List any allergies the individual has. Leave this highlighted to draw attention to it.'}</div>
            )}
          </div>
          <div className="alert-section medical-alert">
            <strong>Medical Alert:</strong><br />
            {editingSection === 'contact-information' ? (
              <textarea 
                value={medicalContactData.medicalAlert}
                onChange={(e) => updateMedicalContactField('medicalAlert', null, e.target.value)}
                placeholder="List any medical alert info others should be aware of i.e. diabetes, seizures, dementia etc. Also leave this highlighted."
              />
            ) : (
              <div>{medicalContactData.medicalAlert || 'List any medical alert info others should be aware of i.e. diabetes, seizures, dementia etc. Also leave this highlighted.'}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element && mainContentRef.current) {
      const container = mainContentRef.current;
      const contentHeader = document.querySelector('.content-header');
      
      // Calculate the height of the sticky header dynamically
      const headerHeight = contentHeader ? contentHeader.offsetHeight : 0;
      const additionalOffset = 10; // Small buffer for visual spacing
      
      const elementTop = element.offsetTop - container.offsetTop;
      container.scrollTo({
        top: elementTop - headerHeight - additionalOffset,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  const handleScroll = () => {
    if (!mainContentRef.current) return;
    
    const container = mainContentRef.current;
    const sections = carePlanSections.flatMap(section => [
      section.id,
      ...section.subsections.map(sub => sub.id)
    ]);

    for (const sectionId of sections) {
      const element = document.getElementById(sectionId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        if (rect.top >= containerRect.top && rect.top <= containerRect.top + 100) {
          setActiveSection(sectionId);
          break;
        }
      }
    }
  };

  useEffect(() => {
    const container = mainContentRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const renderSection = (section) => {
    // Special handling for medical contact information
    if (section.id === 'contact-information') {
      return renderMedicalContactSection();
    }

    // Original section rendering for other sections
    return (
    <div key={section.id} id={section.id} className={`care-section ${section.id === 'personal-biography' ? 'personal-biography-section' : ''}`}>
      <div className="section-header">
        <h2>{section.title}</h2>
        <div className="section-actions">
          {editingSection === section.id ? (
            <div className="edit-actions">
              <button 
                className={`ai-assist-btn ${editingContent.length < 20 ? 'disabled' : ''}`}
                onClick={() => handleAiAssist(section.id)}
                disabled={aiAssisting || editingContent.length < 20 || (showAiPreview && !contentChangedSinceAi)}
                title={editingContent.length < 20 ? 'Add at least 20 characters to use AI assistance' : 'Use AI to enhance this content'}
              >
                {aiAssisting ? '✨ Processing...' : '✨ AI Assist'}
              </button>
              <div className="save-btn-container">
                <button 
                  className="save-btn" 
                  onClick={() => saveSection(section.id)}
                  disabled={saving}
                >
                  {saving ? '💾 Saving...' : '💾 Save'}
                </button>
                {showSaveReminder && (
                  <div className="save-reminder">
                    💡 Remember to save your edits!
                  </div>
                )}
              </div>
              <button className="cancel-btn" onClick={cancelEditing}>
                ❌ Cancel
              </button>
            </div>
          ) : (
            <button 
              className="edit-btn" 
              onClick={() => startEditing(section.id)}
            >
              ✏️ Edit
            </button>
          )}
        </div>
      </div>
      
      {section.subsections.length === 0 ? (
        <div className="section-content">
          <div className="content-area">
            {editingSection === section.id ? (
                <div className="editing-container">
                  {showAiPreview && aiEnhancedContent && (
                    <div className="ai-preview-container">
                      <div className="ai-preview-header">
                        <span className="ai-badge">✨ AI Enhanced Content</span>
                        <div className="ai-preview-actions">
                          <button className="accept-ai-btn" onClick={acceptAiContent}>
                            ✓ Use This
                          </button>
                          <button className="reject-ai-btn" onClick={rejectAiContent}>
                            ✗ Dismiss
                          </button>
                        </div>
                      </div>
                      <div className="ai-preview-content">
                        <div className="preview-section">
                          <h4>Your Original Notes:</h4>
                          <div className="preview-box original">
                            <ReactMarkdown rehypePlugins={[rehypeRaw]}>{originalContentBeforeAi}</ReactMarkdown>
                          </div>
                        </div>
                        <div className="preview-section">
                          <h4>AI Enhanced Version:</h4>
                          <div className="preview-box enhanced">
                            <ReactMarkdown rehypePlugins={[rehypeRaw]}>{aiEnhancedContent}</ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {aiAssisting && (
                    <div className="ai-loading-overlay">
                      <div className="ai-loading-content">
                        <div className="ai-spinner"></div>
                        <p>AI is enhancing your content...</p>
                        <small>This may take a few moments</small>
                      </div>
                    </div>
                  )}
                  <div className="markdown-editor-container">
                    <MDXEditor
                      key={`editor-${editingSection}-${editorKey}`}
                      markdown={editingContent}
                      onChange={handleContentChange}
                      plugins={[
                        toolbarPlugin({
                          toolbarContents: () => (
                            <>
                              <UndoRedo />
                              <Separator />
                              <BoldItalicUnderlineToggles />
                              <Separator />
                              <BlockTypeSelect />
                              <Separator />
                              <ListsToggle />
                              <Separator />
                              <CreateLink />
                              <InsertThematicBreak />
                            </>
                          )
                        }),
                        headingsPlugin(),
                        listsPlugin(),
                        quotePlugin(),
                        thematicBreakPlugin(),
                        markdownShortcutPlugin()
                      ]}
                      className="care-plan-editor"
                      contentEditableClassName="care-plan-content"
                    />
                  </div>
                  {getExplainerText(section) && (
                    <div className="explainer-text editing">
                      <div className="explainer-content">
                        {getExplainerText(section).split('\n').map((line, index) => (
                          <p key={index}>{line || '\u00A0'}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
            ) : (
              <div className="content-display">
                {sectionContents[section.id] ? (
                    <div className="content-with-explainer">
                  <div className="content-text">
                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>{sectionContents[section.id]}</ReactMarkdown>
                      </div>
                      {getExplainerText(section) && (
                        <div className="explainer-container">
                          <button 
                            className="explainer-toggle"
                            onClick={() => toggleExplainer(section.id)}
                          >
                            <div className="toggle-content">
                              <span>View Guidelines</span>
                              <span className={`arrow ${expandedExplainers[section.id] ? 'expanded' : ''}`}>▼</span>
                            </div>
                          </button>
                          {expandedExplainers[section.id] && (
                            <div className="explainer-text collapsed">
                              <div className="explainer-content">
                                {getExplainerText(section).split('\n').map((line, index) => (
                      <p key={index}>{line || '\u00A0'}</p>
                    ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    getExplainerText(section) ? (
                      <div className="explainer-text empty">
                        <div className="explainer-content">
                          {getExplainerText(section).split('\n').map((line, index) => (
                            <p key={index}>{line || '\u00A0'}</p>
                          ))}
                        </div>
                  </div>
                ) : (
                  <p className="placeholder-text">Click edit to add information for {section.title}</p>
                    )
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        section.subsections.map(subsection => {
          // Special handling for vision-hearing subsection
          if (subsection.id === 'vision-hearing') {
            return renderVisionHearingSection();
          }
          
          // Special handling for crisis cycle subsection
          if (subsection.id === 'individualized-crisis-cycle') {
            return renderCrisisCycleSection();
          }
          
          const subsectionKey = `${section.id}-${subsection.id}`;
          return (
            <div key={subsection.id} id={subsection.id} className="subsection">
              <div className="subsection-header">
                <h3>{subsection.title}</h3>
                <div className="subsection-actions">
                  {editingSection === subsectionKey ? (
                    <div className="edit-actions">
                      <button 
                        className={`ai-assist-btn small ${editingContent.length < 20 ? 'disabled' : ''}`}
                        onClick={() => handleAiAssist(section.id, subsection.id)}
                        disabled={aiAssisting || editingContent.length < 20 || (showAiPreview && !contentChangedSinceAi)}
                        title={editingContent.length < 20 ? 'Add at least 20 characters to use AI assistance' : 'Use AI to enhance this content'}
                      >
                        {aiAssisting ? '✨' : '✨ AI Writing Assistant'}
                      </button>
                      <div className="save-btn-container">
                        <button 
                          className="save-btn" 
                          onClick={() => saveSection(section.id, subsection.id)}
                          disabled={saving}
                        >
                          {saving ? '💾 Saving...' : '💾 Save'}
                        </button>
                        {showSaveReminder && (
                          <div className="save-reminder">
                            💡 Remember to save your edits!
                          </div>
                        )}
                      </div>
                      <button className="cancel-btn" onClick={cancelEditing}>
                        ❌ Cancel
                      </button>
                    </div>
                  ) : (
                    <button 
                      className="edit-btn small" 
                      onClick={() => startEditing(section.id, subsection.id)}
                    >
                      ✏️ Edit
                    </button>
                  )}
                </div>
              </div>
              <div className="subsection-content">
                {editingSection === subsectionKey ? (
                    <div className="editing-container">
                      {showAiPreview && aiEnhancedContent && (
                        <div className="ai-preview-container">
                          <div className="ai-preview-header">
                            <span className="ai-badge">✨ AI Enhanced Content</span>
                            <div className="ai-preview-actions">
                              <button className="accept-ai-btn" onClick={acceptAiContent}>
                                ✓ Use This
                              </button>
                              <button className="reject-ai-btn" onClick={rejectAiContent}>
                                ✗ Dismiss
                              </button>
                            </div>
                          </div>
                          <div className="ai-preview-content">
                            <div className="preview-section">
                              <h4>Your Original Notes:</h4>
                              <div className="preview-box original">
                                <ReactMarkdown rehypePlugins={[rehypeRaw]}>{originalContentBeforeAi}</ReactMarkdown>
                              </div>
                            </div>
                            <div className="preview-section">
                              <h4>AI Enhanced Version:</h4>
                              <div className="preview-box enhanced">
                                <ReactMarkdown rehypePlugins={[rehypeRaw]}>{aiEnhancedContent}</ReactMarkdown>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {aiAssisting && (
                        <div className="ai-loading-overlay">
                          <div className="ai-loading-content">
                            <div className="ai-spinner"></div>
                            <p>AI is enhancing your content...</p>
                            <small>This may take a few moments</small>
                          </div>
                        </div>
                      )}
                      <div className="markdown-editor-container">
                        <MDXEditor
                          key={`editor-${editingSection}-${editorKey}`}
                          markdown={editingContent}
                          onChange={handleContentChange}
                          plugins={[
                            toolbarPlugin({
                              toolbarContents: () => (
                                <>
                                  <UndoRedo />
                                  <Separator />
                                  <BoldItalicUnderlineToggles />
                                  <Separator />
                                  <BlockTypeSelect />
                                  <Separator />
                                  <ListsToggle />
                                  <Separator />
                                  <CreateLink />
                                  <InsertThematicBreak />
                                </>
                              )
                            }),
                            headingsPlugin(),
                            listsPlugin(),
                            quotePlugin(),
                            thematicBreakPlugin(),
                            markdownShortcutPlugin()
                          ]}
                          className="care-plan-editor subsection"
                          contentEditableClassName="care-plan-content"
                        />
                      </div>
                      {getExplainerText(section, subsection) && (
                        <div className="explainer-text editing">
                          <div className="explainer-content">
                            {getExplainerText(section, subsection).split('\n').map((line, index) => (
                              <p key={index}>{line || '\u00A0'}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                ) : (
                  <div className="content-display">
                    {sectionContents[subsectionKey] ? (
                        <div className="content-with-explainer">
                      <div className="content-text">
                            <ReactMarkdown rehypePlugins={[rehypeRaw]}>{sectionContents[subsectionKey]}</ReactMarkdown>
                          </div>
                          {getExplainerText(section, subsection) && (
                            <div className="explainer-container">
                              <button 
                                className="explainer-toggle"
                                onClick={() => toggleExplainer(subsectionKey)}
                              >
                                <div className="toggle-content">
                                  <span>View Guidelines</span>
                                  <span className={`arrow ${expandedExplainers[subsectionKey] ? 'expanded' : ''}`}>▼</span>
                                </div>
                              </button>
                              {expandedExplainers[subsectionKey] && (
                                <div className="explainer-text collapsed">
                                  <div className="explainer-content">
                                    {getExplainerText(section, subsection).split('\n').map((line, index) => (
                          <p key={index}>{line || '\u00A0'}</p>
                        ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        getExplainerText(section, subsection) ? (
                          <div className="explainer-text empty">
                            <div className="explainer-content">
                              {getExplainerText(section, subsection).split('\n').map((line, index) => (
                                <p key={index}>{line || '\u00A0'}</p>
                              ))}
                            </div>
                      </div>
                    ) : (
                      <p className="placeholder-text">Click edit to add information for {subsection.title}</p>
                        )
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
  };

  if (loading) {
    return (
      <div className="patient-care-plan">
        <header className="app-header">
          <div className="header-content">
            <div className="logo-section">
              <div className="logo">{appName()}</div>
              {/* <div className="tagline">Plan of Care Organizer</div> */}
            </div>
            <nav className="nav-menu">
              <button className="nav-item" onClick={() => navigate('/')}>Dashboard</button>
              <button className="nav-item" onClick={() => navigate('/individuals')}>Support Plans</button>
              <button className="nav-item profile-btn">👤</button>
            </nav>
          </div>
        </header>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading individual support plan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patient-care-plan">
        <header className="app-header">
          <div className="header-content">
            <div className="logo-section">
              <div className="logo">{appName()}</div>
              {/* <div className="tagline">Plan of Care Organizer</div> */}
            </div>
            <nav className="nav-menu">
              <button className="nav-item" onClick={() => navigate('/')}>Dashboard</button>
              <button className="nav-item" onClick={() => navigate('/individuals')}>Support Plans</button>
              <button className="nav-item profile-btn">👤</button>
            </nav>
          </div>
        </header>
        <div className="error-container">
          <div className="error-message">
            <p>{error}</p>
            <button onClick={loadPatientData} className="retry-btn">Retry</button>
          </div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="patient-care-plan">
        <div className="error-container">
          <p>Patient not found</p>
          <button onClick={() => navigate('/individuals')}>Back to Support Plans</button>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-care-plan">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo">{appName()}</div>
            {/* <div className="tagline">Support Plan Organizer</div> */}
          </div>
          <nav className="nav-menu">
            <button className="nav-item" onClick={() => navigate('/')}>Dashboard</button>
            <button className="nav-item" onClick={() => navigate('/individuals')}>Support Plans</button>
            <button 
              className="nav-item export-btn" 
              onClick={handleExportPDF}
              disabled={exporting}
              title="Export Support Plan as PDF"
            >
              {exporting ? '📄 Exporting...' : '📄 Export PDF'}
            </button>
            <button className="nav-item profile-btn">👤</button>
          </nav>
        </div>
      </header>

      <div className="care-plan-layout">
        {/* Sidebar */}
        <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="patient-info">
              <h3>{patient.name}</h3>
              <p>Age {patient.age} • ID: {patient.patient_id}</p>
            </div>
            <button 
              className="collapse-btn"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              {sidebarCollapsed ? '→' : '←'}
            </button>
          </div>
          
          {!sidebarCollapsed && (
            <nav className="sidebar-nav">
              {carePlanSections.map(section => (
                <div key={section.id} className="nav-section">
                  <div 
                    className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => scrollToSection(section.id)}
                  >
                    {section.title}
                  </div>
                  {section.subsections.length > 0 && (
                    <div className="nav-subsections">
                      {section.subsections.map(subsection => (
                        <div 
                          key={subsection.id}
                          className={`nav-subitem ${activeSection === subsection.id ? 'active' : ''}`}
                          onClick={() => scrollToSection(subsection.id)}
                        >
                          {subsection.title}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          )}
        </aside>

        {/* Main Content */}
        <main className="main-content-area" ref={mainContentRef}>
          <div className="content-header">
            <div className="header-left-section">
              <div className="breadcrumb">
                <button onClick={() => navigate('/individuals')}>← Back to Support Plans</button>
              </div>
              <div className="content-title">
                <h1>{patient.name} - Comprehensive Support Plan</h1>
                <div className="plan-meta">
                  <span className="last-updated">Last updated: {new Date().toLocaleDateString()}</span>
                  <label className="status-editor" title="Set this individual's status">
                    <span className="status-editor-label">Status:</span>
                    <select
                      className={`status-select status-${(patient.status || 'Active').toLowerCase().replace(/\s+/g, '-')}`}
                      value={patient.status || 'Active'}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={statusSaving}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Follow-up Required">Follow-up Required</option>
                    </select>
                    {statusSaving && <span className="status-saving">saving…</span>}
                  </label>
                </div>
              </div>
            </div>
            <div className="header-right-section">
              <ProfilePhotoUpload 
                patientId={id} 
                patientName={patient.name}
                onPhotoUpdate={() => {
                  // Optionally reload patient data or just leave as is
                  console.log('Photo updated');
                }}
              />
            </div>
          </div>

          <div className="scrollable-content">
            {carePlanSections.map(renderSection)}
          </div>
        </main>
      </div>
    </div>
  );
}

export default PatientCarePlan; 